import React, { useState, useCallback } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle, X, Eye } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import { useAuth } from '../../contexts/AuthContext';
import MenuReview from './MenuReview';

interface DocumentUploadProps {
  enterpriseId: string;
  onJobCreated?: (jobId: string) => void;
  onClose?: () => void;
  onBack?: () => void;
}

interface UploadedFile {
  file: File;
  preview?: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  jobId?: string;
  error?: string;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  enterpriseId,
  onJobCreated,
  onClose,
  onBack
}) => {
  const { getIdToken } = useAuth();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reviewingJobId, setReviewingJobId] = useState<string | null>(null);

  const supportedFormats = ['PDF', 'JPG', 'PNG', 'DOCX'];
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const handleFiles = useCallback((newFiles: File[]) => {
    const validFiles = newFiles.filter(file => {
      // Check file size
      if (file.size > maxFileSize) {
        alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      
      // Check file type
      const extension = file.name.split('.').pop()?.toLowerCase();
      const isValidType = ['pdf', 'jpg', 'jpeg', 'png', 'docx'].includes(extension || '');
      
      if (!isValidType) {
        alert(`File ${file.name} is not supported. Please use PDF, JPG, PNG, or DOCX files.`);
        return false;
      }
      
      return true;
    });

    const uploadedFiles: UploadedFile[] = validFiles.map(file => ({
      file,
      status: 'pending',
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined
    }));

    setFiles(prev => [...prev, ...uploadedFiles]);
  }, [maxFileSize]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, [handleFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    handleFiles(selectedFiles);
  };

  const removeFile = (index: number) => {
    setFiles(prev => {
      const newFiles = [...prev];
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview!);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const uploadFile = async (file: File): Promise<string> => {
    const token = await getIdToken();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = reader.result?.toString().split(',')[1];
          if (!base64) {
            throw new Error('Failed to read file');
          }

          const response = await fetch(API_ENDPOINTS.menuSetupUploadDocument(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              enterpriseId,
              sourceType: 'document',
              fileName: file.name,
              fileContent: base64,
              mimeType: file.type
            })
          });

          const result = await response.json().catch(() => null);
          if (!response.ok || !result?.success) {
            const msg = result?.message || result?.error || `Upload failed (${response.status})`;
            throw new Error(msg);
          }
          const jobId = result?.data?.jobId || result?.jobId;
          if (!jobId) throw new Error('Upload response missing jobId');
          resolve(jobId);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.status !== 'pending') continue;

      // Update status to uploading
      setFiles(prev => {
        const newFiles = [...prev];
        newFiles[i] = { ...newFiles[i], status: 'uploading' };
        return newFiles;
      });

      try {
        const jobId = await uploadFile(file.file);
        
        // Update status to success
        setFiles(prev => {
          const newFiles = [...prev];
          newFiles[i] = { ...newFiles[i], status: 'success', jobId };
          return newFiles;
        });

        if (onJobCreated) {
          onJobCreated(jobId);
        }
      } catch (error) {
        // Update status to error
        setFiles(prev => {
          const newFiles = [...prev];
          newFiles[i] = { 
            ...newFiles[i], 
            status: 'error', 
            error: error instanceof Error ? error.message : 'Upload failed'
          };
          return newFiles;
        });
      }
    }

    setUploading(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'uploading':
        return <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'uploading':
        return 'border-blue-200 bg-blue-50';
      case 'success':
        return 'border-green-200 bg-green-50';
      case 'error':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-gray-200 bg-white';
    }
  };

  const hasValidFiles = files.some(f => f.status === 'pending');
  const hasSuccessfulUploads = files.some(f => f.status === 'success');

  // If reviewing a job, show the MenuReview component
  if (reviewingJobId) {
    return (
      <MenuReview 
        jobId={reviewingJobId} 
        onBack={() => setReviewingJobId(null)}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Upload className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                Upload Menu Documents
              </h2>
              <p className="text-gray-600">
                Upload PDFs, images, or documents containing menu information. Our AI will extract items, prices, and descriptions.
              </p>
            </div>
          </div>
          {(onClose || onBack) && (
            <button
              onClick={onClose || onBack}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Supported Formats */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Supported Formats
          </h3>
          <div className="flex flex-wrap gap-2 mb-3">
            {supportedFormats.map(format => (
              <span
                key={format}
                className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full font-medium"
              >
                {format}
              </span>
            ))}
          </div>
          <p className="text-sm text-blue-700 flex items-center">
            <AlertCircle className="w-4 h-4 mr-1" />
            Maximum file size: 10MB per file
          </p>
        </div>
      </div>

      {/* Drag & Drop Area */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div
          className={`
            border-2 border-dashed rounded-lg p-12 text-center transition-all duration-200
            ${dragActive 
              ? 'border-blue-500 bg-blue-50 scale-105' 
              : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
            }
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Upload className="w-8 h-8 text-blue-600" />
            </div>
            <p className="text-xl font-semibold text-gray-700 mb-2">
              Drag & drop your menu files here
            </p>
            <p className="text-gray-500 mb-6">or</p>
            <label className="cursor-pointer">
              <input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.docx"
                onChange={handleFileInput}
                className="hidden"
              />
              <span className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-6 rounded-lg transition-colors shadow-lg hover:shadow-xl">
                Browse Files
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Files to Upload ({files.length})
          </h3>
          <div className="space-y-3">
            {files.map((uploadedFile, index) => (
              <div
                key={index}
                className={`
                  border rounded-lg p-4 flex items-center justify-between transition-all
                  ${getStatusColor(uploadedFile.status)}
                `}
              >
                <div className="flex items-center space-x-3">
                  {uploadedFile.preview ? (
                    <img
                      src={uploadedFile.preview}
                      alt={uploadedFile.file.name}
                      className="w-10 h-10 object-cover rounded"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                      <FileText className="w-5 h-5 text-gray-500" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-900">
                      {uploadedFile.file.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {(uploadedFile.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    {uploadedFile.error && (
                      <p className="text-sm text-red-600 mt-1">
                        {uploadedFile.error}
                      </p>
                    )}
                    {uploadedFile.jobId && (
                      <p className="text-sm text-green-600 mt-1">
                        Job ID: {uploadedFile.jobId}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(uploadedFile.status)}
                  {uploadedFile.status === 'pending' && (
                    <button
                      onClick={() => removeFile(index)}
                      className="p-1 text-gray-500 hover:text-red-500 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {uploadedFile.status === 'success' && uploadedFile.jobId && (
                    <button
                      onClick={() => setReviewingJobId(uploadedFile.jobId!)}
                      className="p-1 text-blue-500 hover:text-blue-700 transition-colors"
                      title="Review extracted items"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {(hasValidFiles || hasSuccessfulUploads) && (
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              {hasSuccessfulUploads && (
                <button
                  onClick={() => {
                    const successfulJob = files.find(f => f.status === 'success');
                    if (successfulJob?.jobId) {
                      setReviewingJobId(successfulJob.jobId);
                    }
                  }}
                  className="bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-6 rounded-lg transition-colors shadow-lg hover:shadow-xl mr-3 flex items-center"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Review Extracted Items
                </button>
              )}
            </div>
            <div>
              {hasValidFiles && (
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className={`
                    font-medium py-3 px-6 rounded-lg transition-colors shadow-lg hover:shadow-xl flex items-center
                    ${uploading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }
                  `}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload Files'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Processing Info */}
      {uploading && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-yellow-500 border-t-transparent mr-3"></div>
              <div>
                <p className="font-medium text-yellow-800">Processing your files...</p>
                <p className="text-sm text-yellow-700">
                  This may take 2-5 minutes depending on file size and complexity.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;